import { Box, ListItemIcon, MenuItem, Popover } from "@mui/material";
import { default as React, useEffect, useState } from "react";
import ControlChart1 from "../../../assets/ControlChart1.svg";
import ControlChart2 from "../../../assets/ControlChart2.svg";
import ControlChart3 from "../../../assets/ControlChart3.svg";
import ControlChart4 from "../../../assets/ControlChart4.svg";
import CustomHead from "../../../components/Common/CustomHead";
import CustomSkeleton from "../../../components/Common/CustomSkeleton";
import { customDateConvert } from "../../../components/Common/Utils/Utils";
import useApi from "../../../service/api.service";
import OverlayGraph from "./OverlayGraph";
import SeperateGraph from "./SeperateGraph";
import { useAuthContext } from "../../../context/AuthProvider";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import toast, { Toaster } from "react-hot-toast";
import * as XLSX from "xlsx";
import TableSkeleton from "../../operator/Components/TableSkeleton";

function RightContainer({
  selectedItemIds,
  setDownloadAllow,
  dateRange,
  setDateRange,
  finalDate,
  setFinalDate,
  takeScreenshot,
  type = "",
}) {
  const filterContainerStyle = {
    flex: 1,
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  };
  const textStyle = {
    fontSize: "12px",
    marginRight: "10px",
    fontWeight: "500",
  };
  const styles = {
    headerBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      // mb: 1,
      height: "70px",
      p: 1,
    },
    titleTypography: {
      display: "flex",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
    },
  };
  const labelDivStyle = { fontSize: "12px", color: "#717171" };
  const labelMainDivStyle = {
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };
  const { QCFull } = useAuthContext();
  const API = useApi();
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState("Overlay");
  const [selectedGranularity, setSelectedGranularity] = useState("Minute");
  const [showScrapPart, setShowScrapPart] = useState(true);

  const fetchData = async () => {
    const customParams = {
      factorIds: JSON.stringify(selectedItemIds),
      factorType: type,
    };

    const requestBodyParams = {
      factorIds: JSON.stringify(selectedItemIds),
      granularity: selectedGranularity,
      startDateTime: finalDate.startDate
        ? customDateConvert(
            new Date(finalDate.startDate).toLocaleDateString("en-US"),
            "YYYY-MM-DD"
          )
        : null,
      endDateTime: finalDate.endDate
        ? customDateConvert(
            new Date(finalDate.endDate).toLocaleDateString("en-US"),
            "YYYY-MM-DD"
          )
        : null,
    };

    try {
      setLoading(true);
      const response = await API.getControlChart(
        customParams,
        requestBodyParams
      );
      setChartData(response?.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        if (selectedItemIds.length > 0) {
          await fetchData();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchDataAsync();
  }, [selectedItemIds, selectedGranularity, finalDate]);

  useEffect(() => {
    const downloadData = chartData?.fData?.map((item, index) => {
      const download = item?.actualValues?.map((data, index1) => ({
        Timestamp: data?.[0],
        "Actual Values": data?.[1],
        "Average Values": item?.averageValues?.[0]?.[1],
        // "Factor Name": item?.factorName,
      }));
      return { data: download, factorName: item?.factorName };
    });
    setExcelData(downloadData);
  }, [chartData]);

  const handleDownload = () => {
    const wb = XLSX.utils.book_new();
    excelData.forEach((item) => {
      const sheet = XLSX.utils.json_to_sheet(item?.data);
      setColumnWidths(sheet, item?.data);
      XLSX.utils.book_append_sheet(wb, sheet, item?.factorName);
    });

    XLSX.writeFile(
      wb,
      `Control Charts Data From ${customDateConvert(
        new Date(finalDate.endDate).toLocaleDateString("en-US"),
        "YYYY-MM-DD"
      )} To ${customDateConvert(
        new Date(finalDate.startDate).toLocaleDateString("en-US"),
        "YYYY-MM-DD"
      )}.xlsx`
    );
    toast.success("Data downloaded successfully");
  };

  const setColumnWidths = (worksheet, data) => {
    const columnHeaders = Object.keys(data[0]);
    const columnWidths = columnHeaders.map((header) => {
      const headerLength = header.length;
      const maxLength = data.reduce((max, row) => {
        const valueLength = row[header] ? row[header].toString().length : 0;
        return Math.max(max, valueLength);
      }, headerLength);
      return { wch: maxLength };
    });
    worksheet["!cols"] = columnWidths;
  };

  const [filterParts, setFilterParts] = useState("All");

  const [toggleDateFilter, setToggleDateFilter] = useState(false);

  const [value, setValue] = useState(1);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenDownload = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <div style={{ height: "100%" }}>
      <CustomHead
        title={""}
        dateRange={dateRange}
        setDateRange={setDateRange}
        value={value}
        setValue={setValue}
        finalDate={finalDate}
        setFinalDate={setFinalDate}
        page={"Metrices"}
        downloadPermission={false}
        setDownloadAllow={setDownloadAllow}
        selectedLayout={selectedLayout}
        setSelectedLayout={setSelectedLayout}
        selectedGranularity={selectedGranularity}
        setSelectedGranularity={setSelectedGranularity}
        handleOpenDownload={handleOpenDownload}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ marginTop: "8px" }}
      >
        <div
          style={{
            backgroundColor: "#fff",
            color: "#000",
            boxShadow: "#0000005f 0px 1px 12px",
            borderRadius: "5px",
          }}
        >
          <MenuItem
            onClick={() => {
              takeScreenshot();
              handleClose();
            }}
          >
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            PDF
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleDownload();
              handleClose();
            }}
          >
            <ListItemIcon>
              <ListAltIcon fontSize="small" />
            </ListItemIcon>
            CSV
          </MenuItem>
        </div>
      </Popover>
      <Box
        sx={{
          borderTop: "2px solid #DEDEDE",
          height: `calc(100%-90px)`,
          overflow: "auto",
        }}
      >
        {loading ? (
          <CustomSkeleton height={"70vh"} />
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "5px",
              }}
            >
              <div
                style={{
                  ...labelMainDivStyle,
                  cursor: "pointer",
                }}
                onClick={() => setShowScrapPart(!showScrapPart)}
              >
                <img
                  src={ControlChart1}
                  alt="logo"
                  width={"15px"}
                  height={"15px"}
                />
                <div
                  style={{
                    fontSize: "12px",
                    color: showScrapPart ? "#717171" : "#8080804D",
                  }}
                >
                  Scrap Part
                </div>
              </div>
              <div style={labelMainDivStyle}>
                <img
                  src={ControlChart2}
                  alt="logo"
                  width={"15px"}
                  height={"15px"}
                />
                <div style={labelDivStyle}>Factor Actual Value</div>
              </div>
              <div style={labelMainDivStyle}>
                <img
                  src={ControlChart3}
                  alt="logo"
                  width={"15px"}
                  height={"15px"}
                />
                <div style={labelDivStyle}>Factor Control Limits</div>
              </div>
              <div style={labelMainDivStyle}>
                <img
                  src={ControlChart4}
                  alt="logo"
                  width={"15px"}
                  height={"15px"}
                />
                <div style={labelDivStyle}>Factor Mean</div>
              </div>
            </div>
            {loading ? (
              <TableSkeleton width="300" />
            ) : (
              <>
                {selectedLayout === "Overlay" ? (
                  <OverlayGraph
                    selectedGranularity={selectedGranularity}
                    newData={chartData?.fData}
                    scrapDate={
                      chartData?.partsData?.[0]?.timestamp
                        ? chartData?.partsData?.[0]?.timestamp
                        : []
                    }
                    showScrapPart={showScrapPart}
                    categories={chartData?.categories}
                  />
                ) : (
                  <SeperateGraph
                    selectedGranularity={selectedGranularity}
                    newData={chartData?.fData}
                    scrapDate={
                      chartData?.partsData?.[0]?.timestamp
                        ? chartData?.partsData?.[0]?.timestamp
                        : []
                    }
                    showScrapPart={showScrapPart}
                    categories={chartData?.categories}
                  />
                )}
              </>
            )}
          </>
        )}
      </Box>
      <Toaster />
    </div>
  );
}

export default RightContainer;
